import { useState, useEffect } from 'react';
import { useData } from './DataContext.jsx';
import { Avatar } from './components.jsx';
import { IconFire, IconTrophy } from './icons.jsx';
import { MfaSettingsCard } from './Mfa.jsx';
import { MFA_ENABLED } from './lib/featureFlags.js';

const AVATAR_COLORS = ['#FF6B4A','#10B981','#F59E0B','#6366F1','#EC4899','#0EA5E9','#B45309','#8B5CF6'];

function SettingsPage() {
  const { currentProfile, isAdmin, updateProfile, signOut, gamification } = useData();

  const [fullName, setFullName] = useState(currentProfile?.full_name || '');
  const [initials, setInitials] = useState(currentProfile?.initials || '');
  const [avatarColor, setAvatarColor] = useState(currentProfile?.avatar_color || AVATAR_COLORS[0]);
  const [hourlyRate, setHourlyRate] = useState(String(currentProfile?.hourly_rate ?? 120));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // 'saved' | 'error' | null
  const [err, setErr] = useState(null);

  // Reset baseline whenever the underlying profile changes (e.g. after save)
  useEffect(() => {
    if (!currentProfile) return;
    setFullName(currentProfile.full_name || '');
    setInitials(currentProfile.initials || '');
    setAvatarColor(currentProfile.avatar_color || AVATAR_COLORS[0]);
    setHourlyRate(String(currentProfile.hourly_rate ?? 120));
  }, [currentProfile?.id]);

  const dirty =
    fullName.trim() !== (currentProfile?.full_name || '') ||
    initials.trim().toUpperCase() !== (currentProfile?.initials || '') ||
    avatarColor !== (currentProfile?.avatar_color || '') ||
    Number(hourlyRate) !== Number(currentProfile?.hourly_rate ?? 120);

  const canSave = dirty && fullName.trim().length >= 2 && Number(hourlyRate) >= 0;

  async function save() {
    if (!canSave || saving) return;
    setSaving(true); setErr(null); setStatus(null);
    try {
      await updateProfile({
        full_name: fullName.trim(),
        initials: initials.trim().toUpperCase() || fullName.trim()[0].toUpperCase(),
        avatar_color: avatarColor,
        hourly_rate: Number(hourlyRate) || 0,
      });
      setStatus('saved');
      setTimeout(() => setStatus(null), 2500);
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setStatus('error');
    } finally {
      setSaving(false);
    }
  }

  const previewUser = {
    full_name: fullName || '—',
    initials: (initials || fullName[0] || '?').toUpperCase(),
    avatar_color: avatarColor,
  };

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <h1>Einstellungen</h1>
        <div className="topbar-spacer"/>
      </div>
      <div className="content" style={{ maxWidth: 760 }}>
        {/* Profil */}
        <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
          <div className="card-title"><span>Profil</span></div>

          <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 20 }}>
            <Avatar user={previewUser} size={64}/>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{fullName || '—'}</div>
              <div className="tiny muted">{currentProfile?.email}</div>
            </div>
          </div>

          <div className="grid grid-2" style={{ gap: 16 }}>
            <div className="field">
              <label>Anzeigename *</label>
              <input className="input" value={fullName} onChange={e => setFullName(e.target.value)}
                     placeholder="Vor- und Nachname"/>
            </div>
            <div className="field">
              <label>Initialen <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>2 Zeichen</span></label>
              <input className="input" value={initials} maxLength={3}
                     onChange={e => setInitials(e.target.value.toUpperCase())}
                     placeholder="wird aus Namen abgeleitet"/>
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Avatar-Farbe</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {AVATAR_COLORS.map(c => (
                  <button key={c} onClick={() => setAvatarColor(c)} style={{
                    width: 32, height: 32, borderRadius: 8,
                    border: avatarColor === c ? '2px solid var(--ink)' : '2px solid transparent',
                    background: c, cursor: 'pointer', padding: 0, outline: 0,
                  }}/>
                ))}
              </div>
            </div>
            <div className="field">
              <label>Stundensatz <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 500 }}>€/h</span></label>
              <input className="input" type="number" min="0" step="5"
                     value={hourlyRate} onChange={e => setHourlyRate(e.target.value)}/>
            </div>
          </div>
        </div>

        {/* Fortschritt */}
        {gamification && (
          <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
            <div className="card-title"><span>Fortschritt</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: 'var(--accent-soft)', color: 'var(--accent)',
                display: 'grid', placeItems: 'center',
              }}><IconTrophy size={26}/></div>
              <div style={{ flex: 1 }}>
                <div className="eyebrow">Aktuelles Level</div>
                <div className="serif" style={{ fontSize: 24, fontWeight: 500, marginTop: 2 }}>{gamification.level}</div>
                <div className="tiny muted" style={{ marginTop: 2 }}>
                  {gamification.xp} XP gesamt
                </div>
              </div>
              <div className="ic" style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 14 }}>
                <IconFire size={14}/> {gamification.streak} Tage
              </div>
            </div>
            {gamification.nextLevel ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div className="tiny muted">Bis <b style={{ color: 'var(--ink-2)' }}>{gamification.nextLevel}</b></div>
                  <div className="mono tiny" style={{ color: 'var(--ink-2)' }}>
                    {gamification.xpInLevel} / {gamification.xpLevelSpan} XP
                  </div>
                </div>
                <div className="bar thick">
                  <span style={{ width: `${gamification.progress}%`, background: 'var(--accent)' }}/>
                </div>
                <div className="tiny muted" style={{ marginTop: 8 }}>
                  Noch <b style={{ color: 'var(--ink-2)' }}>{gamification.xpForNext} XP</b> — das sind ca. {Math.ceil(gamification.xpForNext / 10)} weitere Track-Tage.
                </div>
              </>
            ) : (
              <div className="tiny muted">🏆 Höchste Stufe erreicht.</div>
            )}
            <hr className="div"/>
            <div style={{ display: 'flex', gap: 24 }}>
              <div>
                <div className="eyebrow">Erfasste Tage</div>
                <div className="serif" style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>{gamification.trackedDays}</div>
              </div>
              <div>
                <div className="eyebrow">Frühaufsteher</div>
                <div className="serif" style={{ fontSize: 20, fontWeight: 500, marginTop: 2 }}>{gamification.earlyDays}</div>
                <div className="tiny muted">Start vor 9 Uhr</div>
              </div>
              <div>
                <div className="eyebrow">Aktuelle Streak</div>
                <div className="serif" style={{ fontSize: 20, fontWeight: 500, marginTop: 2, color: 'var(--accent)' }}>{gamification.streak}</div>
                <div className="tiny muted">Werktage in Folge</div>
              </div>
            </div>
            <div className="tiny muted" style={{ marginTop: 16, lineHeight: 1.5, paddingTop: 12, borderTop: '1px solid var(--stroke)' }}>
              XP wird automatisch aus deinen Zeiteinträgen berechnet. Pro Tag mit mind. 30 Min erfasster Zeit: <b style={{ color: 'var(--ink-2)' }}>+10 XP</b>. Pro Frühaufsteher-Tag (Eintrag vor 9 Uhr): <b style={{ color: 'var(--ink-2)' }}>+5 XP</b> zusätzlich.
            </div>
          </div>
        )}

        {/* Account */}
        <div className="card card-pad-lg">
          <div className="card-title"><span>Account</span></div>
          <div className="grid grid-2" style={{ gap: 16 }}>
            <div className="field">
              <label>E-Mail</label>
              <input className="input" value={currentProfile?.email || ''} disabled
                     style={{ opacity: 0.7 }}/>
            </div>
            <div className="field">
              <label>Rolle</label>
              <input className="input" value={isAdmin ? 'Admin' : 'Member'} disabled
                     style={{ opacity: 0.7 }}/>
            </div>
          </div>
          <div className="tiny muted" style={{ marginTop: 12, lineHeight: 1.5 }}>
            E-Mail-Änderungen und Rolle werden zentral durch einen Admin in Supabase verwaltet.
          </div>
          <hr className="div"/>
          <button className="btn" onClick={signOut}
                  style={{ color: 'var(--crit)', borderColor: 'rgba(239,68,68,0.3)' }}>
            Abmelden
          </button>
        </div>

        {/* Sicherheit / 2FA */}
        {MFA_ENABLED && (
          <div style={{ marginTop: 20 }}>
            <MfaSettingsCard/>
          </div>
        )}

        {/* Save bar */}
        <div style={{
          position: 'sticky', bottom: 24, marginTop: 20,
          padding: '12px 16px', borderRadius: 12,
          background: 'var(--bg-elev)',
          border: '1px solid var(--stroke-2)',
          boxShadow: 'var(--shadow)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div className="tiny muted" style={{ flex: 1 }}>
            {status === 'saved' && <span style={{ color: 'var(--ok)' }}>✓ Gespeichert</span>}
            {status === 'error' && <span style={{ color: 'var(--crit)' }}>{err}</span>}
            {!status && (dirty ? 'Ungespeicherte Änderungen' : 'Keine Änderungen')}
          </div>
          <button className="btn btn-primary" onClick={save} disabled={!canSave || saving}
                  style={{ opacity: (canSave && !saving) ? 1 : 0.45 }}>
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </>
  );
}

export { SettingsPage };
